import { Store } from '@betgames/bg-state-manager';
import { IMessage, IProfile, IRoom, IStoreMessage } from './interfaces';
import { t } from '../Translate';

interface IChatStoreState {
    messages: IStoreMessage[];
    selfProfile: IProfile | null;
    profiles: Record<string, IProfile>;
    room: IRoom | null;
    isLoggedIn: boolean;
    isLoading: boolean;
    error: {
        message: string;
        isBlocking: boolean;
        isAdmin: boolean;
    };
}

class ChatStoreSupabase extends Store<IChatStoreState> {
    constructor() {
        super({
            key: 'ChatStoreSupabase',
            default: {
                messages: [],
                selfProfile: null,
                profiles: {},
                room: null,
                isLoggedIn: false,
                isLoading: true,
                error: {
                    message: '',
                    isBlocking: false,
                    isAdmin: false,
                },
            },
        });
    }

    private checkProfileStatus(profile: IProfile): boolean {
        if (profile.status !== 'ACTIVE') {
            this.setError({
                message: t.string('chat_blocked_user_message'),
                isBlocking: true,
                isAdmin: true,
            });
            return false;
        }

        return true;
    }

    public setAuth(profile: IProfile) {
        this.updateProfile(profile);
        const isActive = this.setSelfProfile(profile);

        if (isActive) {
            this.update((draft) => {
                draft.isLoggedIn = true;
            });
        }

        this.setIsLoading(false);
    }

    public setIsLoading(isLoading: boolean) {
        if (isLoading) {
            this.resetError();
        }

        this.update((draft) => {
            draft.isLoading = isLoading;
        });
    }

    public setError({
        message,
        isBlocking = true,
        isAdmin = false,
    }: Partial<IChatStoreState['error']>) {
        if (this.value.isLoading) {
            this.setIsLoading(false);
        }

        this.update((draft) => {
            draft.error = {
                message,
                isBlocking,
                isAdmin,
            };
        });
    }

    public resetError() {
        this.update((draft) => {
            draft.error = {
                message: '',
                isBlocking: false,
                isAdmin: false,
            };
        });
    }

    public setMessages(messages: IMessage[]) {
        this.update((draft) => {
            draft.messages = messages as IStoreMessage[];
        });
    }

    public addMessage(message: IMessage) {
        this.update((draft) => {
            draft.messages.unshift(message as IStoreMessage);
        });
    }

    public addFailedMessage(content: string, errorMessage: string) {
        const message: Partial<IStoreMessage> = {
            id: Date.now().toString(),
            content,
            errorMessage: t.string(errorMessage || 'chat_message_error'),
            created_at: new Date().toISOString(),
            profile_id: this.value.selfProfile?.id || '',
            display_name: this.value.selfProfile?.display_name || '',
        };

        this.addMessage(message as IMessage);
    }

    public updateMessage(messageId: string, updatedMessage: Partial<IMessage>) {
        this.update((draft) => {
            const messageIndex = draft.messages.findIndex((message) => message.id === messageId);
            if (messageIndex !== -1) {
                draft.messages[messageIndex] = {
                    ...draft.messages[messageIndex],
                    ...updatedMessage,
                };
            }
        });
    }

    public removeMessage(messageId: string) {
        this.update((draft) => {
            draft.messages = draft.messages.filter((message) => message.id !== messageId);
        });
    }

    public setSelfProfile(profile: IProfile): boolean {
        const isActive = this.checkProfileStatus(profile);

        this.update((draft) => {
            draft.selfProfile = profile;
        });

        return isActive;
    }

    public setRoom(room: IRoom) {
        this.update((draft) => {
            draft.room = room;
        });
    }

    public getCachedProfileById(profileId: string): IProfile | undefined {
        return this.value.profiles[profileId];
    }

    public updateProfile(profiles: IProfile | IProfile[]) {
        const profileArray = Array.isArray(profiles) ? profiles : [profiles];

        this.update((draft) => {
            profileArray.forEach((profile) => {
                if (profile.id === draft.selfProfile?.id) {
                    this.setSelfProfile(profile);
                    return;
                }

                draft.profiles[profile.id] = profile;
            });
        });
    }

    public getDisplayName(profileId: string): string {
        const profile = this.getCachedProfileById(profileId);
        return profile?.display_name || 'Unknown User';
    }

    public reset(): void {
        this.update((draft) => {
            draft.messages = [];
            draft.room = null;
            draft.profiles = {};
            draft.selfProfile = null;
            draft.isLoggedIn = false;
            draft.isLoading = true;
        });
        this.resetError();
    }

    public get selfProfile(): IProfile | null {
        return this.value.selfProfile;
    }

    public get room(): IRoom | null {
        return this.value.room;
    }
}

export const chatStoreSupabase = new ChatStoreSupabase();
